import { useLayoutEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/shared/cn';
import type { ControlChartCaption, ControlChartViewModel } from '@/versions/v1/shared/SpcEngine';
import { formatSpcValue } from '@/versions/v1/shared/SpcEngine';
import { readingByIdLookup } from '@/versions/v1/shared/reading.derive';
import { parseTime } from '@/versions/v1/shared/clock.derive';
import type { ControlLimitSet, SpcPhase } from '@/versions/v1/schema/spc';
import type { Band } from '@/versions/v1/schema/reading';
import { vizToneColor } from './viz-tone';
import { controlChartYDomain, flaggedPointTone } from './ControlChart.transform';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export type { ControlChartCaption };

/** Shared chart input; analytics frames can supply their own points and three limits. */
export type ControlChartModel = Pick<ControlChartViewModel, 'readingLabel' | 'points'> & {
  readingId?: string;
  phase?: SpcPhase;
  limits?: Pick<ControlLimitSet, 'mean' | 'ucl' | 'lcl'> &
    Partial<Pick<ControlLimitSet, 'sigma'>>;
  spec?: Band;
  /** Fixed Control Plan warning; independent of statistical UCL/LCL and the spec. */
  bandHigh?: number;
  /** Time interval to shade behind the series when the engine names an excursion segment. */
  excursionRange?: { startAt: string; endAt: string };
  /** Timestamp window used for proportional x positions when supplied by an analytics surface. */
  timeWindow?: { startAt: string; endAt: string };
  unit?: string;
  precision?: number;
  caption?: ControlChartCaption;
  learningCaption?: string;
};

export type ControlChartProps = {
  model: ControlChartModel;
  height?: number;
  className?: string;
  hideLabel?: boolean;
  /** Keep adjacent statistical-limit labels readable in compact charts. */
  hideMeanLabel?: boolean;
  /** Exact y-domain for a surface that must show a specific comparison; unset preserves the chart's auto-domain. */
  yDomain?: { min: number; max: number };
  /** Draw both Control Plan bounds. Existing callers keep the single legacy spec edge by default. */
  showBothSpecLimits?: boolean;
  /** Move the unit away from y tick labels in charts where the axis labels are dense. */
  unitLabelPosition?: 'legacy' | 'top';
  /** With `showBothSpecLimits`, print each Control Plan bound's value at the line's right end. Default off. */
  specLimitLabels?: boolean;
  /** `highlighted` draws a point marker only on highlighted points, so a dense series reads as a thin line. Default `all`. */
  pointMarkers?: 'all' | 'highlighted';
  /** Break the series where consecutive points are far apart (over 4× the median spacing) and join the gap with a faint dotted connector. Default off. */
  breakGaps?: boolean;
  /** `even` places time ticks on even clock steps across `model.timeWindow`; `points` (default) keeps the point-index ticks. */
  timeTicks?: 'points' | 'even';
  /** Tick, unit and edge-label size in px. Default 9 keeps existing callers; 10 meets the DESIGN.md micro floor. */
  tickLabelPx?: 9 | 10;
  /** The series is always ink (DESIGN.md § Crimson: crimson never draws a data series). `ink` is kept for callers that name it. */
  seriesTone?: 'ink';
  /** `dashed` draws the centre line dashed. Default `solid`. */
  meanLine?: 'solid' | 'dashed';
  /** Name the two control limits (`UCL` / `LCL`) at the right end of their lines. Default off. */
  limitLabels?: boolean;
  /** `change` prints the day only where it changes (`07 Jun 12:00`, `20:00`,
   *  `08 Jun 04:00`, `11:00`) instead of on every multi-day tick, so a narrow
   *  chart fits more ticks without collision. Default `each` — every existing
   *  caller renders byte-identically. */
  timeTickDay?: 'each' | 'change';
  /** Draw at least this many evenly spaced time ticks when such a set fits
   *  without collision; falls back to the chart's own picker when it does not.
   *  Default off. */
  minTimeTicks?: number;
};

const PAD = { top: 12, right: 44, bottom: 28, left: 36 };
const EVEN_TICK_STEPS_MINUTES = [15, 30, 60, 120, 180, 240, 360, 720];

/** Parse a series stamp as UTC whether or not it carries a zone, so tick labels read the stamp's own clock. */
function utcMs(at: string): number {
  return Date.parse(/(?:Z|[+-]\d{2}:?\d{2})$/.test(at) ? at : `${at.length === 16 ? `${at}:00` : at}Z`);
}

/** Clock ticks on an even step inside the window (at least two, at most five), as window fractions. */
function evenTimeTicks(timeWindow: { startAt: string; endAt: string }): { fraction: number; label: string }[] {
  const startMs = utcMs(timeWindow.startAt);
  const endMs = utcMs(timeWindow.endAt);
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) return [];
  const multiDay = timeWindow.startAt.slice(0, 10) !== timeWindow.endAt.slice(0, 10);
  for (const step of EVEN_TICK_STEPS_MINUTES) {
    const stepMs = step * 60_000;
    const first = Math.ceil(startMs / stepMs) * stepMs;
    const ticks: { fraction: number; label: string }[] = [];
    for (let at = first; at <= endMs; at += stepMs) {
      const iso = new Date(at).toISOString().slice(0, 16);
      ticks.push({ fraction: (at - startMs) / (endMs - startMs), label: timeTickLabel(iso, multiDay) });
    }
    if (ticks.length >= 2 && ticks.length <= 5) return ticks;
  }
  return [];
}

/** Split a series into runs wherever a spacing exceeds 4× the median spacing. */
function seriesRuns(points: readonly { at: string }[]): number[][] {
  const indices = points.map((_, index) => index);
  if (points.length < 3) return [indices];
  const steps = points.slice(1).map((point, index) => Date.parse(point.at) - Date.parse(points[index]!.at));
  if (steps.some((step) => !Number.isFinite(step))) return [indices];
  const sorted = [...steps].sort((a, b) => a - b);
  const median = sorted[Math.floor(sorted.length / 2)]!;
  if (median <= 0) return [indices];
  const runs: number[][] = [[0]];
  steps.forEach((step, index) => {
    if (step > median * 4) runs.push([index + 1]);
    else runs[runs.length - 1]!.push(index + 1);
  });
  return runs;
}
/** Below this height the chart is a sparkline: no tick labels, minimal padding. */
const SPARKLINE_HEIGHT = 48;

function timeTickLabel(at: string, multiDay: boolean): string {
  if (!multiDay) return at.split('T')[1]?.slice(0, 5) ?? at;
  const d = parseTime(at);
  const day = String(d.getUTCDate()).padStart(2, '0');
  const mon = MONTHS[d.getUTCMonth()];
  const hm = at.split('T')[1]?.slice(0, 5) ?? '';
  return `${day} ${mon} ${hm}`;
}

function windowCrossesMidnight(points: readonly { at: string }[]): boolean {
  if (points.length < 2) return false;
  const first = parseTime(points[0]!.at);
  const last = parseTime(points[points.length - 1]!.at);
  return (
    first.getUTCDate() !== last.getUTCDate() ||
    first.getUTCMonth() !== last.getUTCMonth() ||
    first.getUTCFullYear() !== last.getUTCFullYear()
  );
}

/** Evenly spaced tick indices reaching `count`, first and last included. */
function evenTickIndices(length: number, count: number): number[] {
  if (count < 2 || length < 2) return [0];
  const indices = new Set<number>();
  for (let i = 0; i < count; i += 1) {
    indices.add(Math.min(length - 1, Math.ceil((i * (length - 1)) / (count - 1))));
  }
  return [...indices];
}

/** Keep the first and last tick and every middle whose label box — anchored as
 *  the final list anchors it (`start` / `middle` / `end`) — clears its
 *  neighbour. `dropCollidingTicks` judges a candidate as if it were the last
 *  label, which under-keeps beside the first tick; this is the reducer for the
 *  opt-in even-count path, where the requested count is the point. */
function keepNonCollidingTicks(
  rows: readonly { index: number; label: string }[],
  toX: (index: number) => number,
  measureWidth: (text: string) => number,
): { index: number; label: string }[] {
  if (rows.length <= 2) return [...rows];
  const last = rows[rows.length - 1]!;
  const lastBox = tickLabelBox(toX(last.index), 'end', last.label, measureWidth);
  const kept: { index: number; label: string }[] = [rows[0]!];
  for (const row of rows.slice(1, -1)) {
    const previous = kept[kept.length - 1]!;
    const previousBox = tickLabelBox(
      toX(previous.index),
      kept.length === 1 ? 'start' : 'middle',
      previous.label,
      measureWidth,
    );
    const box = tickLabelBox(toX(row.index), 'middle', row.label, measureWidth);
    if (box.left < previousBox.right + TICK_LABEL_GAP) continue;
    if (box.right + TICK_LABEL_GAP > lastBox.left) continue;
    kept.push(row);
  }
  kept.push(last);
  return kept;
}

const TICK_LABEL_GAP = 6;
/** Fallback when SVG measure is unavailable — verified ~4.75 px/glyph at 9px Inter. */
const TICK_GLYPH_WIDTH_FALLBACK = 4.75;

function tickLabelBox(
  x: number,
  anchor: 'start' | 'middle' | 'end',
  label: string,
  measureWidth: (text: string) => number,
): { left: number; right: number } {
  const width = measureWidth(label);
  if (anchor === 'start') return { left: x, right: x + width };
  if (anchor === 'end') return { left: x - width, right: x };
  return { left: x - width / 2, right: x + width / 2 };
}

function tickAnchor(index: number, total: number): 'start' | 'middle' | 'end' {
  if (index === 0) return 'start';
  if (index === total - 1) return 'end';
  return 'middle';
}

function ticksCollide(
  ticks: readonly { index: number; label: string }[],
  toX: (index: number) => number,
  measureWidth: (text: string) => number,
): boolean {
  for (let i = 1; i < ticks.length; i += 1) {
    const prev = tickLabelBox(
      toX(ticks[i - 1]!.index),
      tickAnchor(i - 1, ticks.length),
      ticks[i - 1]!.label,
      measureWidth,
    );
    const curr = tickLabelBox(
      toX(ticks[i]!.index),
      tickAnchor(i, ticks.length),
      ticks[i]!.label,
      measureWidth,
    );
    if (curr.left < prev.right + TICK_LABEL_GAP) return true;
  }
  return false;
}

/** Drop middle ticks when label boxes would overlap; first and last keep priority. */
function dropCollidingTicks(
  candidates: { index: number; label: string }[],
  toX: (index: number) => number,
  measureWidth: (text: string) => number,
): { index: number; label: string }[] {
  if (candidates.length <= 2) return candidates;
  const first = candidates[0]!;
  const last = candidates[candidates.length - 1]!;
  const middles = candidates.slice(1, -1);
  const kept: { index: number; label: string }[] = [first];
  for (const tick of middles) {
    const trial = [...kept, tick];
    if (!ticksCollide(trial, toX, measureWidth)) kept.push(tick);
  }
  let result = [...kept, last];
  while (result.length > 2 && ticksCollide(result, toX, measureWidth)) {
    result = [result[0]!, ...result.slice(2, -1), result[result.length - 1]!];
  }
  return result;
}

function pickTimeLabels(
  points: readonly { at: string }[],
  width: number,
  measureWidth: (text: string) => number,
  timeWindow?: { startAt: string; endAt: string },
  options: { minCount?: number; dayStyle?: 'each' | 'change' } = {},
): { index: number; label: string }[] {
  if (points.length === 0) return [];
  const multiDay = windowCrossesMidnight(points);
  const innerW = width - PAD.left - PAD.right;
  const startMs = timeWindow ? Date.parse(timeWindow.startAt) : NaN;
  const endMs = timeWindow ? Date.parse(timeWindow.endAt) : NaN;
  const toX = (index: number) => {
    if (Number.isFinite(startMs) && Number.isFinite(endMs) && endMs > startMs) {
      const atMs = Date.parse(points[index]!.at);
      if (Number.isFinite(atMs)) return PAD.left + ((atMs - startMs) / (endMs - startMs)) * innerW;
    }
    return PAD.left + (points.length <= 1 ? innerW / 2 : (index / (points.length - 1)) * innerW);
  };

  // Label a candidate index list: `change` keeps the day only on the first tick
  // of each day, so a narrow multi-day chart can carry more labels. Adjacent
  // duplicates still collapse, exactly as `dedupeAdjacentLabels` did.
  const build = (indices: readonly number[]) => {
    const seen = new Set<number>();
    const rows: { index: number; label: string }[] = [];
    let previousDay: string | null = null;
    for (const index of indices) {
      if (index < 0 || index >= points.length || seen.has(index)) continue;
      seen.add(index);
      const at = points[index]!.at;
      const day = at.slice(0, 10);
      const label =
        multiDay && options.dayStyle === 'change' && day === previousDay
          ? at.split('T')[1]?.slice(0, 5) ?? at
          : timeTickLabel(at, multiDay);
      previousDay = day;
      if (rows.length > 0 && rows[rows.length - 1]!.label === label) continue;
      rows.push({ index, label });
    }
    return rows;
  };

  if (options.minCount && options.minCount > 2) {
    const wanted = keepNonCollidingTicks(
      build(evenTickIndices(points.length, options.minCount)),
      toX,
      measureWidth,
    );
    if (wanted.length >= options.minCount) {
      if (options.dayStyle !== 'change') return wanted;
      // Re-label the kept sequence so a dropped day-first tick cannot leave a
      // bare clock on the glass; fall back when the longer labels no longer fit.
      const relabelled = build(wanted.map((row) => row.index));
      return ticksCollide(relabelled, toX, measureWidth) ? wanted : relabelled;
    }
  }

  const indices =
    points.length <= 4
      ? points.map((_, index) => index)
      : [
          0,
          Math.floor((points.length - 1) / 3),
          Math.floor(((points.length - 1) * 2) / 3),
          points.length - 1,
        ];
  return dropCollidingTicks(build(indices), toX, measureWidth);
}

function formatCaptionLine(caption: ControlChartCaption): string {
  const parts = [
    `mean ${caption.mean}`,
    `UCL ${caption.ucl}`,
    `LCL ${caption.lcl}`,
    `σ ${caption.sigma}`,
  ];
  if (caption.specBound) parts.push(caption.specBound);
  if (caption.rule) parts.push(caption.rule);
  if (caption.conforming) parts.push(caption.conforming);
  if (caption.cpk) parts.push(`Cpk ${caption.cpk}`);
  return parts.join(' · ');
}

export function ControlChart({
  model,
  height = 160,
  className,
  hideLabel,
  hideMeanLabel = false,
  yDomain,
  showBothSpecLimits = false,
  unitLabelPosition = 'legacy',
  specLimitLabels = false,
  pointMarkers = 'all',
  breakGaps = false,
  timeTicks = 'points',
  tickLabelPx = 9,
  meanLine = 'solid',
  limitLabels = false,
  timeTickDay = 'each',
  minTimeTicks,
}: ControlChartProps) {
  const tickText = tickLabelPx === 10 ? 'text-[10px]' : 'text-[9px]';
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const measureRef = useRef<SVGTextElement | null>(null);
  const [width, setWidth] = useState(640);
  const [measureReady, setMeasureReady] = useState(false);
  const reading = model.readingId ? readingByIdLookup(model.readingId) : undefined;

  useLayoutEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const measure = () => setWidth(Math.max(240, Math.round(el.getBoundingClientRect().width)));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useLayoutEffect(() => {
    if (measureRef.current) setMeasureReady(true);
  }, [width]);

  const measureWidth = (text: string): number => {
    const node = measureRef.current;
    if (node && measureReady) {
      node.textContent = text;
      return node.getComputedTextLength();
    }
    return text.length * TICK_GLYPH_WIDTH_FALLBACK;
  };

  const values = model.points.map((p) => p.value);
  const limits = model.limits;
  const computedYDomain = controlChartYDomain(model, yDomain);
  const paddedMin = computedYDomain.min;
  const paddedMax = computedYDomain.max;
  const sparkline = height < SPARKLINE_HEIGHT;
  const innerW = width - PAD.left - PAD.right;
  const innerH = sparkline ? height - 6 : height - PAD.top - PAD.bottom;
  const windowStartMs = model.timeWindow ? Date.parse(model.timeWindow.startAt) : NaN;
  const windowEndMs = model.timeWindow ? Date.parse(model.timeWindow.endAt) : NaN;
  const toX = (index: number) => {
    if (Number.isFinite(windowStartMs) && Number.isFinite(windowEndMs) && windowEndMs > windowStartMs) {
      const atMs = Date.parse(model.points[index]!.at);
      if (Number.isFinite(atMs)) return PAD.left + ((atMs - windowStartMs) / (windowEndMs - windowStartMs)) * innerW;
    }
    return PAD.left + (values.length <= 1 ? innerW / 2 : (index / (values.length - 1)) * innerW);
  };
  const toY = (value: number) =>
    (sparkline ? 3 : PAD.top) + innerH - ((value - paddedMin) / (paddedMax - paddedMin)) * innerH;

  const excursionStart = model.excursionRange
    ? model.points.findIndex((point) => point.at >= model.excursionRange!.startAt)
    : -1;
  const excursionEnd = model.excursionRange
    ? model.points.reduce((found, point, index) =>
        point.at <= model.excursionRange!.endAt ? index : found, -1)
    : -1;
  const excursionStartX = model.timeWindow && model.excursionRange
    ? PAD.left + ((Date.parse(model.excursionRange.startAt) - windowStartMs) / (windowEndMs - windowStartMs)) * innerW
    : excursionStart >= 0 ? toX(excursionStart) : -1;
  const excursionEndX = model.timeWindow && model.excursionRange
    ? PAD.left + ((Date.parse(model.excursionRange.endAt) - windowStartMs) / (windowEndMs - windowStartMs)) * innerW
    : excursionEnd >= 0 ? toX(excursionEnd) : -1;

  const specLo = model.spec?.lo;
  const specHi = model.spec?.hi;
  const specEdgeY =
    specLo !== undefined && specLo >= paddedMin && specLo <= paddedMax
      ? toY(specLo)
      : specHi !== undefined && specHi >= paddedMin && specHi <= paddedMax
        ? toY(specHi)
        : null;

  const formatValue = (value: number) =>
    reading
      ? formatSpcValue(reading, value)
      : value.toLocaleString('en-US', {
          minimumFractionDigits: model.precision ?? 2,
          maximumFractionDigits: model.precision ?? 2,
        });
  const yTicks = limits
    ? [
        { key: 'lcl', value: limits.lcl },
        ...(hideMeanLabel ? [] : [{ key: 'mean', value: limits.mean }]),
        { key: 'ucl', value: limits.ucl },
      ].map(({ value }) => ({
        value,
        y: toY(value),
        label: formatValue(value),
      }))
    : [];

  const evenTicks = !sparkline && timeTicks === 'even' && model.timeWindow ? evenTimeTicks(model.timeWindow) : [];
  const timeLabels = sparkline || evenTicks.length > 0
    ? []
    : pickTimeLabels(model.points, width, measureWidth, model.timeWindow, {
        minCount: minTimeTicks,
        dayStyle: timeTickDay,
      });
  const runs = breakGaps ? seriesRuns(model.points) : [values.map((_, index) => index)];

  return (
    <div ref={wrapperRef} className={cn('w-full space-y-1', className)}>
      {!hideLabel ? (
        <p className="text-xs font-medium text-zinc-900 dark:text-zinc-100">{model.readingLabel}</p>
      ) : null}
        {model.phase === 'learning' ? (
        <p className="text-xs text-muted">{model.learningCaption}</p>
      ) : null}
      <svg viewBox={`0 0 ${width} ${height}`} width={width} height={height} className="block max-w-full">
        <text
          ref={measureRef}
          x={-9999}
          y={-9999}
          className={`fill-current ${tickText} text-muted tabular-nums`}
          aria-hidden
        />
        {excursionStartX >= PAD.left && excursionEndX >= excursionStartX ? (
          <rect
            data-excursion-shading="true"
            x={Math.max(PAD.left, excursionStartX - 3)}
            y={sparkline ? 1 : PAD.top}
            width={Math.max(6, Math.min(width - PAD.right, excursionEndX + 3) - Math.max(PAD.left, excursionStartX - 3))}
            height={sparkline ? innerH + 4 : innerH}
            fill="color-mix(in srgb, var(--state-warning) 12%, transparent)"
          />
        ) : null}
        {limits ? (
          <>
            {limits.sigma !== undefined && limits.sigma > 0 ? (
              <rect
                x={PAD.left}
                y={toY(limits.mean + limits.sigma)}
                width={innerW}
                height={Math.max(0, toY(limits.mean - limits.sigma) - toY(limits.mean + limits.sigma))}
                data-sigma-band="true"
                fill="var(--ink-tertiary)"
                opacity={0.08}
              />
            ) : null}
            <line x1={PAD.left} x2={width - PAD.right} y1={toY(limits.ucl)} y2={toY(limits.ucl)} stroke="var(--ink-tertiary)" strokeDasharray="4 3" />
            <line x1={PAD.left} x2={width - PAD.right} y1={toY(limits.lcl)} y2={toY(limits.lcl)} stroke="var(--ink-tertiary)" strokeDasharray="4 3" />
            <line x1={PAD.left} x2={width - PAD.right} y1={toY(limits.mean)} y2={toY(limits.mean)} stroke="var(--ink-tertiary)" strokeWidth={meanLine === 'dashed' ? 1.25 : 1.5} strokeDasharray={meanLine === 'dashed' ? '5 4' : undefined} data-mean-line={meanLine === 'dashed' ? 'dashed' : undefined} />
            {limitLabels && !sparkline
              ? ([['UCL', limits.ucl], ['LCL', limits.lcl]] as const).map(([name, value]) => (
                  <text key={name} data-limit-label={name} x={width - PAD.right + 4} y={toY(value) + 3} className={`fill-current ${tickText} text-muted`}>
                    {name}
                  </text>
                ))
              : null}
          </>
        ) : null}
        {model.bandHigh !== undefined ? (
          <line data-band-high={model.bandHigh} x1={PAD.left} x2={width - PAD.right} y1={toY(model.bandHigh)} y2={toY(model.bandHigh)} stroke={vizToneColor('warn')} strokeDasharray="5 3" />
        ) : null}
        {showBothSpecLimits ? (
          <>
            {specLo !== undefined && specLo >= paddedMin && specLo <= paddedMax ? (
              <line data-spec-limit="low" x1={PAD.left} x2={width - PAD.right} y1={toY(specLo)} y2={toY(specLo)} stroke={vizToneColor('warn')} strokeDasharray="2 2" />
            ) : null}
            {specHi !== undefined && specHi >= paddedMin && specHi <= paddedMax ? (
              <line data-spec-limit="high" x1={PAD.left} x2={width - PAD.right} y1={toY(specHi)} y2={toY(specHi)} stroke={vizToneColor('warn')} strokeDasharray="2 2" />
            ) : null}
            {specLimitLabels
              ? ([['low', specLo], ['high', specHi]] as const).map(([edge, bound]) =>
                  bound !== undefined && bound >= paddedMin && bound <= paddedMax ? (
                    <text
                      key={edge}
                      data-spec-limit-label={edge}
                      x={width - PAD.right + 4}
                      y={toY(bound) + 3}
                      fill={vizToneColor('warn')}
                      className="text-[10px] font-medium tabular-nums"
                    >
                      {formatValue(bound)}
                    </text>
                  ) : null)
              : null}
          </>
        ) : specEdgeY !== null ? (
          <>
            <line
              x1={PAD.left}
              x2={width - PAD.right}
              y1={specEdgeY}
              y2={specEdgeY}
              stroke={vizToneColor('warn')}
              strokeDasharray="2 2"
            />
            <text x={width - PAD.right + 2} y={specEdgeY + 3} className={`fill-current ${tickText} text-muted`}>
              spec
            </text>
          </>
        ) : null}
        {values.length > 1
          ? runs.map((run, runIndex) => (
              <g key={`run-${run[0]}`}>
                {runIndex > 0 ? (
                  <line
                    data-series-gap="true"
                    x1={toX(runs[runIndex - 1]!.at(-1)!)}
                    y1={toY(values[runs[runIndex - 1]!.at(-1)!]!)}
                    x2={toX(run[0]!)}
                    y2={toY(values[run[0]!]!)}
                    stroke="var(--ink-tertiary)"
                    strokeDasharray="1 3"
                    strokeLinecap="round"
                  />
                ) : null}
                {run.length > 1 ? (
                  <polyline
                    fill="none"
                    stroke="var(--ink-primary)"
                    strokeWidth={1.25}
                    strokeLinejoin={breakGaps ? 'round' : undefined}
                    points={run.map((index) => `${toX(index)},${toY(values[index]!)}`).join(' ')}
                  />
                ) : null}
              </g>
            ))
          : null}
        {values.map((value, index) => {
          const point = model.points[index]!;
          const highlighted = point.highlighted;
          if (pointMarkers === 'highlighted' && !highlighted) return null;
          return (
            <circle
              key={point.at}
              cx={toX(index)}
              cy={toY(value)}
              r={highlighted ? 4 : 3}
              data-flagged-point={highlighted ? flaggedPointTone(value, limits) : undefined}
              fill={highlighted ? vizToneColor(flaggedPointTone(value, limits)) : 'var(--ink-primary)'}
            />
          );
        })}
        {yTicks.map((tick) => (
          <g key={tick.label}>
            <line x1={PAD.left - 4} x2={PAD.left} y1={tick.y} y2={tick.y} stroke="var(--ink-tertiary)" />
            <text data-y-tick="true" x={PAD.left - 6} y={tick.y + 3} textAnchor="end" className={`fill-current ${tickText} text-muted tabular-nums`}>
              {tick.label}
            </text>
          </g>
        ))}
        {(model.unit ?? reading?.unit) && yTicks.length > 0 && unitLabelPosition === 'top' ? (
          <text data-axis-unit="true" x={PAD.left - 6} y={PAD.top - 3} textAnchor="end" className={`fill-current ${tickText} text-muted`}>
            {model.unit ?? reading?.unit}
          </text>
        ) : (model.unit ?? reading?.unit) && yTicks.length > 0 ? (
          <text
            data-axis-unit="true"
            x={PAD.left - 6}
            y={yTicks[yTicks.length - 1]!.y + 12}
            textAnchor="end"
            className={`fill-current ${tickText} text-muted`}
          >
            {model.unit ?? reading?.unit}
          </text>
        ) : null}
        {evenTicks.map((tick) => {
          const x = PAD.left + tick.fraction * innerW;
          return (
            <g key={tick.label} data-time-tick="even">
              <line x1={x} x2={x} y1={PAD.top + innerH} y2={PAD.top + innerH + 3} stroke="var(--ink-tertiary)" />
              <text x={x} y={height - 8} textAnchor="middle" className="fill-current text-[10px] text-muted tabular-nums">
                {tick.label}
              </text>
            </g>
          );
        })}
        {timeLabels.map((row, tickIndex) => (
          <text
            key={row.index}
            x={toX(row.index)}
            y={height - 6}
            textAnchor={
              tickIndex === timeLabels.length - 1
                ? 'end'
                : tickIndex === 0
                  ? 'start'
                  : 'middle'
            }
            className={`fill-current ${tickText} text-muted tabular-nums`}
          >
            {row.label}
          </text>
        ))}
      </svg>
      {model.phase !== 'learning' && model.caption ? (
        <p className="text-xs text-muted tabular-nums">{formatCaptionLine(model.caption)}</p>
      ) : null}
    </div>
  );
}

export function ChartExpanderButton({
  expanded,
  onToggle,
  label,
}: {
  expanded: boolean;
  onToggle: () => void;
  label: string;
}) {
  const Icon = expanded ? ChevronDown : ChevronRight;
  return (
    <button
      type="button"
      className="inline-flex items-center text-accent-700 dark:text-accent-400"
      aria-expanded={expanded}
      aria-label={label}
      onClick={onToggle}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden />
    </button>
  );
}
